import { Star } from "lucide-react";
import { MyTransactionItem } from "../../hooks/useBalance";
import { useFormatDate } from "../../hooks/date";

interface PointsResultProps {
  amount: number;
  value: number;
  // Account movements to list under the balance. Optional so callers that only
  // have the totals still render the card. When provided, the history table is
  // shown below the totals (backend already calculated each row's balance).
  transactions?: MyTransactionItem[];
}

// Shared presentation of a customer's points balance. Used by the myPoints page
// (SearchMyPoints) and the chat assistant (ChatBox) so the two render
// identically and cannot drift.
const PointsResult = ({
  amount,
  value,
  transactions,
}: PointsResultProps): JSX.Element => {
  const formatDate = useFormatDate();

  return (
    <div className="space-y-6 mt-6">
      {/* Total Points Card */}
      <div className="bg-gradient-to-br from-primary to-primary-dark rounded-2xl p-8 text-center text-white shadow-strong">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Star className="h-6 w-6 fill-white" />
          <h3 className="text-lg font-semibold uppercase tracking-wide">
            TUS PUNTOS DISPONIBLES
          </h3>
        </div>
        <div className="text-6xl font-bold mb-2">
          {Number(amount).toFixed(2)}
        </div>
        <div className="text-xl opacity-90">
          Equivalente a ${Number(value).toFixed(2)}
        </div>
      </div>

      {/* Thank you message */}
      <p className="text-center text-lg text-muted-foreground">
        ¡Gracias por tu visita, esperamos verte pronto!
      </p>

      {/* Transactions Section */}
      {transactions && transactions.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-2xl font-bold text-foreground">
            Movimientos de tu cuenta
          </h3>

          {/* Transactions Table */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-border">
                  <th className="text-left py-3 px-4 text-primary font-semibold">
                    Cuándo
                  </th>
                  <th className="text-left py-3 px-4 text-primary font-semibold">
                    Abono
                  </th>
                  <th className="text-left py-3 px-4 text-primary font-semibold">
                    Cargo
                  </th>
                  <th className="text-left py-3 px-4 text-primary font-semibold">
                    Saldo
                  </th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((item) => {
                  const isExchange = item.type === "exchange";
                  const value = isExchange
                    ? item.amount / item.exchange_rate
                    : item.amount;
                  const formattedValue = Number(value).toFixed(2);

                  return (
                    <tr
                      key={new Date(item.transaction_dt).getTime()}
                      className="border-b border-border hover:bg-muted/30 transition-colors"
                    >
                      <td className="py-3 px-4 text-foreground">
                        {formatDate(item.transaction_dt)}
                      </td>
                      <td className="py-3 px-4 text-green-600 font-medium">
                        {!isExchange ? `+${formattedValue} puntos` : ""}
                      </td>
                      <td className="py-3 px-4 text-red-600 font-medium">
                        {isExchange ? `-${formattedValue} puntos` : ""}
                      </td>
                      <td className="py-3 px-4 text-foreground font-bold">
                        {Number(item.balance).toFixed(2)} puntos
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PointsResult;
